import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { carpoolsAPI, usersAPI } from '../services/api';
import Modal from '../components/Modal';
import MapPicker from '../components/MapPicker';
import InviteModal from '../components/InviteModal';
import RouteMap from '../components/RouteMap';
import { PageLoading } from '../components/appPage';
import { getActivityLabel } from '../utils/carpoolUi';
import { UserPlus, Users, Baby, MapPin, Navigation2, CircleDot, ArrowLeft, Clock, PhoneCall, Trash2, Pencil } from 'lucide-react';
import './CarpoolDesignPrototype.css';

const CarpoolDetailsModern = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [carpool, setCarpool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showDeleteCarpoolModal, setShowDeleteCarpoolModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [pickupPoints, setPickupPoints] = useState({ carpool: [], defaults: [] });
  const [userChildren, setUserChildren] = useState([]);
  const [showPickupModal, setShowPickupModal] = useState(false);
  const [pickupForm, setPickupForm] = useState({
    childId: '',
    label: '',
    address: '',
    latitude: null,
    longitude: null,
    isDefault: false
  });
  const [editingPickup, setEditingPickup] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [pickupPendingRemove, setPickupPendingRemove] = useState(null);

  useEffect(() => {
    loadCarpool();
    loadUserChildren();
    // Get current user ID from token
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserId(payload.userId);
      } catch (e) {
        console.error('Failed to parse token:', e);
      }
    }
  }, [id]);

  const loadCarpool = async () => {
    try {
      const response = await carpoolsAPI.getCarpool(id);
      setCarpool(response.data.carpool);
      await loadPickupPoints();
    } catch (error) {
      console.error('Failed to load carpool:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPickupPoints = async () => {
    try {
      const response = await carpoolsAPI.getPickupPoints(id);
      setPickupPoints(response.data.pickupPoints || { carpool: [], defaults: [] });
    } catch (error) {
      console.error('Failed to load pickup points:', error);
    }
  };

  const loadUserChildren = async () => {
    try {
      const response = await usersAPI.getChildren();
      setUserChildren(response.data.children || []);
    } catch (error) {
      console.error('Failed to load user children:', error);
    }
  };

  const handleLeaveCarpool = async () => {
    setActionLoading(true);
    try {
      await carpoolsAPI.leaveCarpool(id);
      navigate('/carpools');
    } catch (error) {
      setErrorMessage(error.response?.data?.error?.message || t('errors.generic'));
      setShowErrorModal(true);
      setShowLeaveModal(false);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCarpool = async () => {
    setActionLoading(true);
    setShowDeleteCarpoolModal(false);
    try {
      await carpoolsAPI.deleteCarpool(id);
      navigate('/carpools');
    } catch (error) {
      setErrorMessage(error.response?.data?.error?.message || t('errors.generic'));
      setShowErrorModal(true);
    } finally {
      setActionLoading(false);
    }
  };

  /** Default pickup for the child from profile (defaults), not carpool start */
  const resolveDefaultPickupForChild = (childId) => {
    if (!childId) return null;
    const defaults = pickupPoints.defaults || [];
    const forChild = defaults.filter((p) => p.child?.id === childId);
    if (forChild.length === 0) return null;
    return forChild.find((p) => p.isDefault) || forChild[0];
  };

  const buildPickupFormForChild = (childId, existingPoint = null) => {
    if (existingPoint) {
      return {
        childId: existingPoint.child?.id || childId || '',
        label: existingPoint.label || '',
        address: existingPoint.address || '',
        latitude:
          existingPoint.latitude != null ? Number(existingPoint.latitude) : null,
        longitude:
          existingPoint.longitude != null ? Number(existingPoint.longitude) : null,
        isDefault: Boolean(existingPoint.isDefault)
      };
    }
    const def = resolveDefaultPickupForChild(childId);
    const lat =
      def?.latitude != null
        ? Number(def.latitude)
        : carpool?.startLatitude != null
          ? Number(carpool.startLatitude)
          : null;
    const lng =
      def?.longitude != null
        ? Number(def.longitude)
        : carpool?.startLongitude != null
          ? Number(carpool.startLongitude)
          : null;
    return {
      childId: childId || '',
      label: def?.label || '',
      address: def?.address || '',
      latitude: lat,
      longitude: lng,
      isDefault: false
    };
  };

  const openPickupModal = (pickupPoint = null) => {
    const inCarpool = new Set((pickupPoints.carpool || []).map((p) => p.child?.id).filter(Boolean));
    const selectable = pickupPoint
      ? userChildren
      : userChildren.filter((c) => !inCarpool.has(c.id));

    if (!pickupPoint && carpool) {
      const cap = carpool.childSeatsPerCar ?? 3;
      if ((pickupPoints.carpool?.length || 0) >= cap) {
        setErrorMessage(t('carpools.childSeatsFull'));
        setShowErrorModal(true);
        return;
      }
      if (selectable.length === 0) {
        setErrorMessage(t('carpools.noChildrenToAdd'));
        setShowErrorModal(true);
        return;
      }
    }

    const initialChildId = pickupPoint?.child?.id || selectable[0]?.id || '';
    setPickupForm(
      pickupPoint
        ? buildPickupFormForChild(initialChildId, pickupPoint)
        : buildPickupFormForChild(initialChildId, null)
    );
    setEditingPickup(pickupPoint);
    setShowPickupModal(true);
  };

  const handlePickupDelete = async (pickupPointId) => {
    try {
      await carpoolsAPI.deletePickupPoint(id, pickupPointId);
      await loadPickupPoints();
      await loadCarpool();
      setSuccessMessage(t('carpools.pickupDeleteSuccess'));
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Delete pickup point error:', error);
      setErrorMessage(error.response?.data?.error?.message || t('errors.generic'));
      setShowErrorModal(true);
    }
  };

  const handlePickupSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!pickupForm.childId) {
        setErrorMessage(t('carpools.selectChildRequired'));
        setShowErrorModal(true);
        return;
      }

      if (!pickupForm.latitude || !pickupForm.longitude) {
        setErrorMessage(t('carpools.pickupLocationRequired'));
        setShowErrorModal(true);
        return;
      }

      const payload = {
        childId: pickupForm.childId,
        label: pickupForm.label || undefined,
        address: pickupForm.address,
        latitude: pickupForm.latitude,
        longitude: pickupForm.longitude,
        isDefault: pickupForm.isDefault
      };

      if (editingPickup) {
        await carpoolsAPI.updatePickupPoint(id, editingPickup.id, payload);
      } else {
        await carpoolsAPI.createPickupPoint(id, payload);
      }

      await loadPickupPoints();
      setShowPickupModal(false);
      setShowSuccessModal(true);
      setSuccessMessage(editingPickup ? t('carpools.pickupUpdateSuccess') : t('carpools.pickupCreateSuccess'));
    } catch (error) {
      console.error('Pickup point error:', error);
      setErrorMessage(error.response?.data?.error?.message || t('errors.generic'));
      setShowErrorModal(true);
    }
  };

  const hasLocationSupport = useMemo(() => Boolean(process.env.REACT_APP_GOOGLE_MAPS_API_KEY), []);
  const startLocation = useMemo(() => {
    if (!carpool?.startLatitude || !carpool?.startLongitude) return null;
    return { lat: Number(carpool.startLatitude), lng: Number(carpool.startLongitude) };
  }, [carpool]);
  const endLocation = useMemo(() => {
    if (!carpool?.endLatitude || !carpool?.endLongitude) return null;
    return { lat: Number(carpool.endLatitude), lng: Number(carpool.endLongitude) };
  }, [carpool]);

  const validWaypoints = useMemo(() => {
    return (pickupPoints?.carpool || [])
      .filter(p => p.latitude && p.longitude)
      .map(p => ({
        id: p.id,
        lat: Number(p.latitude),
        lng: Number(p.longitude),
        address: p.address,
        label: p.label,
        childName: p.child?.name || ''
      }));
  }, [pickupPoints]);

  const childIdsInCarpool = useMemo(
    () => new Set((pickupPoints.carpool || []).map((p) => p.child?.id).filter(Boolean)),
    [pickupPoints]
  );

  const { seatCapacity, enrolledChildren, freeSeats } = useMemo(() => {
    const cap = carpool?.childSeatsPerCar ?? 3;
    const en = pickupPoints.carpool?.length || 0;
    return { seatCapacity: cap, enrolledChildren: en, freeSeats: Math.max(0, cap - en) };
  }, [carpool, pickupPoints]);

  const selectableChildrenForPickup = useMemo(() => {
    if (editingPickup) return userChildren;
    return userChildren.filter((c) => !childIdsInCarpool.has(c.id));
  }, [editingPickup, userChildren, childIdsInCarpool]);

  const handleOpenNavigation = () => {
    if (!startLocation || !endLocation) return;
    
    const baseUrl = "https://www.google.com/maps/dir/?api=1";
    const originStr = `&origin=${startLocation.lat},${startLocation.lng}`;
    const destStr = `&destination=${endLocation.lat},${endLocation.lng}`;
    
    let waypointsStr = "";
    if (validWaypoints && validWaypoints.length > 0) {
      const wpCoords = validWaypoints.map(wp => `${wp.lat},${wp.lng}`).join('|');
      waypointsStr = `&waypoints=${wpCoords}`;
    }
    
    const finalUrl = `${baseUrl}${originStr}${destStr}${waypointsStr}&travelmode=driving`;
    window.open(finalUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="carpool-details-page page-shell">
        <PageLoading message={t('common.loading')} />
      </div>
    );
  }

  if (!carpool) {
    return (
      <div className="carpool-details-page page-shell">
        <div className="empty-state">
          <h3>Carpool not found</h3>
        </div>
      </div>
    );
  }

  const isCreator = carpool.createdBy === currentUserId;

  const phoneForChildParent = (childUserId) => {
    const m = carpool.members?.find((mem) => mem.user?.id === childUserId);
    const raw = m?.user?.phone;
    if (!raw || typeof raw !== 'string') return null;
    const digits = raw.replace(/[^\d+]/g, '');
    return digits.length >= 6 ? raw.trim() : null;
  };

  return (
    <div className="page-shell page-shell--fluid carpool-modern-root">
      <div className="carpool-modern-shell">
        <div className="dribbble-map-header">
          <div className="dribbble-map-header__map">
            {startLocation && endLocation ? (
              <RouteMap
                variant="hero"
                origin={startLocation}
                destination={endLocation}
                waypoints={validWaypoints}
              />
            ) : (
              <div className="carpool-map-empty">
                {t('carpools.routeMapNeedEndpoints')}
              </div>
            )}
          </div>
          <div className="glass-nav-bar">
            <Link to="/carpools" className="glass-btn">
              <ArrowLeft size={20} />
            </Link>
            <button
              type="button"
              className="glass-btn"
              onClick={() => setShowInviteModal(true)}
              aria-label={t('carpools.inviteMembers')}
            >
              <UserPlus size={20} />
            </button>
          </div>
          <div className="map-fade-overlay" />
        </div>

        <div className="dribbble-bottom-sheet">
          <div className="sheet-header">
            <div className="sheet-badge">{getActivityLabel(carpool.activity, t)}</div>
            <h1 className="sheet-title">{carpool.name}</h1>
            <p className="sheet-subtitle" style={{marginBottom: '16px'}}>
              <Clock size={14} /> Предстоящо взимане
            </p>
          </div>

          <div className="sheet-participants" style={{ cursor: 'pointer' }}>
            <div className="avatar-stack">
              {carpool.members?.slice(0, 4).map(m => (
                 <img key={m.id} src={m.user.profilePhoto || ("https://i.pravatar.cc/100?img=" + ((m.user.id * 7) % 50))} className="dribbble-avatar" alt={m.user.firstName} />
              ))}
              {carpool.members?.length > 4 && <div className="dribbble-avatar more-avatar">+{carpool.members.length - 4}</div>}
            </div>
            <div className="participant-info">
              <h3>Вие сте {isCreator ? 'организатор' : 'член'}</h3>
              <p>{carpool.members?.length} родители в групата</p>
            </div>
            <div className="action-circle">
               <Users size={18} />
            </div>
          </div>

          <div className="route-timeline">
            <div className="carpool-seat-info-bar" role="status" aria-live="polite">
              <div className="carpool-seat-info-bar__main">
                <div className="carpool-seat-info-bar__icon-wrap" aria-hidden>
                  <Baby size={20} strokeWidth={2} />
                </div>
                <div className="carpool-seat-info-bar__text">
                  <span className="carpool-seat-info-bar__label">{t('carpools.childSeatsTitle')}</span>
                  <span className="carpool-seat-info-bar__stats">
                    {t('carpools.childSeatsSummary', {
                      used: enrolledChildren,
                      total: seatCapacity,
                      free: freeSeats
                    })}
                  </span>
                </div>
              </div>
              {freeSeats > 0 ? (
                <button
                  type="button"
                  className="action-circle carpool-seat-info-bar__add"
                  onClick={() => openPickupModal()}
                  aria-label={t('carpools.addChildToRoute')}
                  title={t('carpools.addChildToRoute')}
                >
                  <UserPlus size={20} strokeWidth={2} aria-hidden />
                </button>
              ) : (
                <span className="carpool-seat-info-bar__full">{t('carpools.fullBadge')}</span>
              )}
            </div>

            <div className="dribbble-timeline dribbble-timeline--modern">
              <div className="d-timeline-item">
                <div className="d-node">
                  <div className="d-icon-wrapper start">
                    <CircleDot size={18} strokeWidth={3} />
                  </div>
                  <div className="d-line" />
                </div>
                <div className="d-content">
                  <p className="d-time">{t('carpools.timelineStart', 'Старт')}</p>
                  <div className="d-card d-card--modern d-card--start">
                    <span className="d-card__eyebrow">{t('carpools.startPoint')}</span>
                    <p className="d-card__title">{carpool.startAddress || carpool.address || '—'}</p>
                    <p className="d-card__hint">{t('carpools.timelineStartHint', 'Тръгване от тук')}</p>
                  </div>
                </div>
              </div>

              {(pickupPoints.carpool || []).map((point) => {
                const isMine = point.child?.userId === currentUserId;
                const tel = point.child?.userId ? phoneForChildParent(point.child.userId) : null;
                return (
                  <div className="d-timeline-item" key={point.id}>
                    <div className="d-node">
                      <div className="d-icon-wrapper waypoint">
                        <Baby size={16} strokeWidth={2.5} />
                      </div>
                      <div className="d-line" />
                    </div>
                    <div className="d-content">
                      <p className="d-time">{t('carpools.timelineChild', 'Дете')}</p>
                      <div className="d-card d-card--modern d-card--child">
                        <span className="d-card__eyebrow">{t('carpools.timelinePickup', 'Вземане')}</span>
                        <h3 className="d-card__name">{point.child?.name || t('carpools.pickupPoints')}</h3>
                        <p className="d-card__address">{point.address || point.label || '—'}</p>
                        <div className="d-card-actions d-card-actions--modern d-card-actions--circles">
                          {!isMine &&
                            (tel ? (
                              <a
                                href={`tel:${tel.replace(/\s/g, '')}`}
                                className="action-circle action-circle--call"
                                aria-label={t('carpools.callParent', 'Обади се')}
                                title={t('carpools.callParent', 'Обади се')}
                              >
                                <PhoneCall size={18} strokeWidth={2} aria-hidden />
                              </a>
                            ) : (
                              <span
                                className="action-circle action-circle--muted"
                                title={t('carpools.noPhoneForPickup', 'Няма телефон')}
                                role="img"
                                aria-label={t('carpools.noPhoneForPickup', 'Няма телефон')}
                              >
                                <PhoneCall size={18} strokeWidth={2} aria-hidden />
                              </span>
                            ))}
                          {isMine && (
                            <>
                              <button
                                type="button"
                                className="action-circle"
                                onClick={() => openPickupModal(point)}
                                aria-label={t('carpools.changePickupAddress')}
                                title={t('carpools.changePickupAddress')}
                              >
                                <Pencil size={18} strokeWidth={2} aria-hidden />
                              </button>
                              <button
                                type="button"
                                className="action-circle action-circle--danger"
                                onClick={() => setPickupPendingRemove(point)}
                                aria-label={t('carpools.removeChildFromRoute')}
                                title={t('carpools.removeShort', 'Премахни')}
                              >
                                <Trash2 size={18} strokeWidth={2} aria-hidden />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className="d-timeline-item d-timeline-item--last">
                <div className="d-node">
                  <div className="d-icon-wrapper end">
                    <MapPin size={18} strokeWidth={2.5} />
                  </div>
                </div>
                <div className="d-content">
                  <p className="d-time">{t('carpools.timelineEnd', 'Край')}</p>
                  <div className="d-card d-card--modern d-card--end">
                    <span className="d-card__eyebrow">{t('carpools.endPoint')}</span>
                    <p className="d-card__title">{carpool.endAddress || '—'}</p>
                    <p className="d-card__hint">{t('carpools.timelineEndHint', 'Училище / детска градина')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="dribbble-sheet-footer carpool-modern-footer-actions">
             <button type="button" className="dribbble-main-btn" onClick={handleOpenNavigation}>
               <Navigation2 size={20} />
               Старт навигация
             </button>
          </div>

          {isCreator && (
            <div style={{ marginTop: '16px', paddingBottom: '30px' }}>
                <button className="btn-danger" style={{ width: '100%', height: '44px' }} onClick={() => setShowDeleteCarpoolModal(true)}>Изтрий карпула</button>
            </div>
          )}
          {!isCreator && (
             <div style={{ marginTop: '16px', paddingBottom: '30px' }}>
                <button className="btn-danger" style={{ width: '100%', height: '44px' }} onClick={() => setShowLeaveModal(true)}>Напусни карпула</button>
            </div>
          )}

        </div>
      </div>

      <Modal
        isOpen={!!pickupPendingRemove}
        onClose={() => setPickupPendingRemove(null)}
        title={t('carpools.removeChildFromRoute')}
        size="small"
      >
        <div className="confirm-modal-content">
          <p className="confirm-modal-description">
            {t('carpools.removeChildConfirm', { name: pickupPendingRemove?.child?.name || '—' })}
          </p>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={() => setPickupPendingRemove(null)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={async () => {
                if (!pickupPendingRemove) return;
                await handlePickupDelete(pickupPendingRemove.id);
                setPickupPendingRemove(null);
              }}
            >
              {t('common.delete')}
            </button>
          </div>
        </div>
      </Modal>

      <InviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        carpoolId={id}
        carpoolName={carpool.name}
        onInviteSent={loadCarpool}
      />

      {/* Leave Carpool Modal */}
      <Modal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        title="Напускане на карпула"
        size="small"
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon" style={{ background: '#fef2f2', color: '#dc2626' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
          </div>
          <p className="confirm-modal-description">
            Сигурни ли сте, че искате да напуснете карпула "{carpool.name}"?
          </p>
          <div className="modal-footer">
            <button
              className="btn-secondary"
              onClick={() => setShowLeaveModal(false)}
              disabled={actionLoading}
            >
              Отказ
            </button>
            <button
              className="btn-danger"
              onClick={handleLeaveCarpool}
              disabled={actionLoading}
            >
              {actionLoading ? 'Напускане...' : 'Напусни'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Carpool Modal */}
      <Modal
        isOpen={showDeleteCarpoolModal}
        onClose={() => setShowDeleteCarpoolModal(false)}
        title="Изтриване на карпула"
        size="small"
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon" style={{ background: '#fee2e2', color: '#dc2626' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
          </div>
          <p className="confirm-modal-description">
            Сигурни ли сте, че искате да изтриете карпула? Това действие е необратимо.
          </p>
          <div className="modal-footer">
            <button className="btn-secondary" onClick={() => setShowDeleteCarpoolModal(false)}>
              Отказ
            </button>
            <button className="btn-danger" onClick={handleDeleteCarpool} disabled={actionLoading}>
              {actionLoading ? 'Изтриване...' : 'Изтрий карпула'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={t('common.success')}
        size="small"
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon" style={{ background: '#d1fae5', color: '#10b981' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <p className="confirm-modal-description">{successMessage || t('common.success')}</p>
          <div className="modal-footer">
            <button type="button" className="btn-primary" onClick={() => setShowSuccessModal(false)}>
              {t('common.ok')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Pickup Modal */}
      <Modal
        isOpen={showPickupModal}
        onClose={() => {
          setShowPickupModal(false);
          setEditingPickup(null);
        }}
        title={editingPickup ? t('carpools.editPickupPoint') : t('carpools.addChildToRoute')}
        size="medium"
      >
        <form className="pickup-form" onSubmit={handlePickupSubmit}>
          <div className="form-group">
            <label>{t('common.child')}</label>
            <select
              className="form-input"
              value={pickupForm.childId}
              onChange={(e) => {
                const newChildId = e.target.value;
                if (editingPickup) {
                  setPickupForm((prev) => ({ ...prev, childId: newChildId }));
                  return;
                }
                setPickupForm(buildPickupFormForChild(newChildId, null));
              }}
              required
              disabled={selectableChildrenForPickup.length === 0}
            >
              {selectableChildrenForPickup.map((child) => (
                <option key={child.id} value={child.id}>
                  {child.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('children.pickupLabel')}</label>
            <input
              type="text"
              className="form-input"
              value={pickupForm.label}
              onChange={(e) => setPickupForm({ ...pickupForm, label: e.target.value })}
              placeholder={t('children.pickupLabelPlaceholder')}
            />
          </div>
          <div className="form-group">
            <label>{t('children.pickupAddress')}</label>
            {hasLocationSupport ? (
              <div className="selected-address">
                {pickupForm.address ? pickupForm.address : t('children.mapAddressPlaceholder')}
              </div>
            ) : (
              <input
                type="text"
                className="form-input"
                required
                value={pickupForm.address}
                onChange={(e) => setPickupForm((prev) => ({ ...prev, address: e.target.value }))}
                placeholder={t('children.pickupAddressPlaceholder')}
              />
            )}
          </div>
          <MapPicker
            value={pickupForm.latitude && pickupForm.longitude ? { lat: pickupForm.latitude, lng: pickupForm.longitude } : null}
            onChange={(coords) => setPickupForm((prev) => ({ ...prev, latitude: coords.lat, longitude: coords.lng }))}
            addressValue={pickupForm.address}
            onAddressChange={(address) => setPickupForm((prev) => ({ ...prev, address }))}
            helperText={t('children.pickupMapHelp')}
            disabled={!hasLocationSupport}
          />
          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                checked={pickupForm.isDefault}
                onChange={(e) => setPickupForm({ ...pickupForm, isDefault: e.target.checked })}
              />
              {t('children.setAsDefault')}
            </label>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={() => setShowPickupModal(false)}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-primary">
              {t('common.save')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Error Modal */}
      <Modal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={t('errors.error')}
        size="small"
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <p className="confirm-modal-description">{errorMessage}</p>
          <div className="modal-footer">
            <button type="button" className="btn-primary" onClick={() => setShowErrorModal(false)}>
              {t('common.ok')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CarpoolDetailsModern;
